import {ReactElement} from 'react';
import store from '../store/store';
import {getCost} from '../TaskManagement/TaskHelper';
import LookupRegistry from "../ui/LookupSelect/LookupRegistry";
import util from "../lib/util";
import * as DateUtil from "../lib/dateutils";
import FinancialHelper from "../Financial/FinancialHelper";
import FlatFee from '../entities/flatFee';
import FlatFeeMilestone from '../entities/flatFeeMilestone';
import * as FlatFeeMilestoneActions from '../store/actions/flatFeeMilestoneActions';
import * as FlatFeeActions from '../store/actions/flatFeeActions';
import StateLabel from "../ui/StateLabel/StateLabel";
import IconButton from "../ui/IconButton/IconButton";
import Icons from "../ui/IconButton/Icons";
import {sortContacts} from "../Contacts/ContactFormat";
import {getCurrentFirmId, getCurrentUserId} from "../lib/picos/currentLogin";
import newHRTimestamp from "../lib/picos/timestamp";
import {getRedFlags} from "../lib/picos/redFlagHelper";
import Expense from "../entities/expense";
import Task from "../entities/task";
import Matter from "../entities/matter";
import CaseType from "../entities/caseType";
import {EntityType, uuid} from "../@types/entity";
import Contact from "../entities/contact";
import Participant from "../entities/participant";
import Lead from "../entities/lead";
import Transaction from "../entities/transaction";
import RedFlagType from "../entities/redFlagType";
import ArrayMap from "../lib/ArrayMap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {IconProp} from "@fortawesome/fontawesome-svg-core";
import * as CaseTypeActions from "../store/actions/caseTypeActions";
import {genGenericCaseTypeUUID} from "../lib/picos/uuids";

export type FlatFeeResult = {
    date: string;
    title: string;
    amount: number;
    type: 'ff' | 'ffm';
    data: FlatFee | FlatFeeMilestone;
}

/**
 * Common reducer for adding up expenses.
 *
 * @param {number} total
 * @param {Expense} expense
 * @return {number}
 * @private
 */
const _expenseReducer = (total: number, expense: Expense): number => total + (expense.quantity * expense.cost);

/**
 * Common reducer for adding up task costs.
 *
 * @param {number}total
 * @param {Task} task
 * @returns {number}
 * @private
 */
const _taskReducer = (total: number, task: Task): number => total + getCost(task);

/**
 *
 * @param {number} total
 * @param {Expense | Task} item
 * @return {number}
 * @private
 */
const _amountPaidReducer = (total: number, item: Expense | Task): number => total + item.amountPaid;

/**
 * Helper class for matters and invoices.
 * @class
 */
class CaseHelper {

    /** Sentinel code used for generic (category-level) CaseType entries */
    static GENERIC_CODE = '__GENERIC__';

    /** Returns true if a CaseType represents a generic category entry (no subcategory, no real code) */
    static isGeneric(ct: CaseType): boolean {
        return !ct.subcategory && (!ct.code || ct.code === CaseHelper.GENERIC_CODE);
    }

    // ---------------------- G E N E R A L --------------------//

    /**
     * Return the
     * @param {Matter} matter
     */
    static getStatus(matter: Matter): 'Open' | 'Closed' | 'All' {
        if (matter.disposition === 'o') return 'Open';
        else if (matter.disposition === 'c' || matter.disposition === 'w') return 'Closed';
        else return 'All';
    }

    /**
     * Get a fresh matter code.
     *
     * @param {CaseType} caseType
     * @return {string}
     */
    static getMatterCode = (caseType: CaseType): string => {
        // Find the next available sequence for this year + case-type-code prefix.
        //
        // We filter by code prefix instead of caseTypeId because a matter's
        // caseTypeId can be cleared after creation — those matters would then
        // become invisible to a caseTypeId-based scan and we'd reuse their
        // sequence numbers, producing duplicate codes (OWA-438).
        //
        // The code's middle segment IS the case-type code, so prefix-matching
        // identifies exactly the matters that share this series, regardless of
        // whether their caseTypeId still points anywhere.
        const currentYear = new Date().getFullYear();
        const prefix = `${currentYear}-${caseType.code}-`;

        const entries = store.getState().matters
            .filter(m => m.code?.startsWith(prefix));

        // Count matters whose suffix isn't a parseable integer. Preserves the
        // original guard: if N matters share this prefix with non-numeric
        // suffixes, the next auto-numeric value steps past them.
        let entriesWithIndex = 0;

        const highestIndex = entries
            .reduce((highest, matter) => {
                const suffix = matter.code.slice(prefix.length);
                const parsed = Number.parseInt(suffix);
                if (isNaN(parsed)) {
                    entriesWithIndex++;
                    return highest;
                }
                return Math.max(parsed, highest);
            }, 0);

        const index = Math.max(entriesWithIndex, highestIndex);
        const newIndex = ('' + (index + 1)).padStart(4, '0');
        return `${prefix}${newIndex}`;
    };

    /**
     * Get a matter from its ID.
     *
     * @param {uuid} matterId
     * @param {Matter} [unsavedEntity]
     * @return {Matter | undefined} the matter, or undefined when no matter has that id
     */
    static getMatterFromId = (matterId: uuid, unsavedEntity?: Matter): Matter | undefined => {
        if (unsavedEntity && unsavedEntity.id === matterId) return unsavedEntity;
        else return store.getState().matters.get(matterId);
    };

    /**
     * Apply a case-type change to a matter clone.
     *
     * Pure mutation — does not call setCurrent, dispatch redux actions, or trigger
     * dialogs. The caller is responsible for committing the result and invoking any
     * UI side effects (flat-fee seeding/prompts).
     *
     * Sets `caseTypeId` (or null when caseType is null), and when caseType is provided
     * also sets `rateType` from the case type's default. For new cases, additionally
     * seeds `code` (auto-generated) and `minimumBalance` from the case type.
     *
     * @param {Matter} matter - the matter to mutate (typically a clone)
     * @param {CaseType | null} caseType - the picked case type, or null when clearing
     * @param {boolean} isNewCase - true for unsaved/new matters; gates code + minimumBalance seeding
     * @returns {Matter} the mutated matter (returned for ergonomics)
     */
    static applyCaseTypeUpdates = (matter: Matter, caseType: CaseType | null, isNewCase: boolean): Matter => {
        matter.caseTypeId = caseType ? caseType.id : null;

        if (caseType) {
            if (isNewCase) {
                matter.code = CaseHelper.getMatterCode(caseType);
                matter.minimumBalance = caseType.minimumTrust;
            }
            matter.rateType = caseType.rateType;
        }

        return matter;
    };

    /**
     * Return a list of matters where the clients are participants.
     * @param {Array.<uuid>} clientIds
     * @param {boolean} [matchAll] - all clientIds must match!
     * @param {Matter} [unsavedEntity]
     * @return {Matter[]}
     */
    static getMattersForClients = (clientIds: Array<uuid>, matchAll: boolean = false, unsavedEntity?: Matter): Matter[] => {
        if (!clientIds) return [];

        // Find all matters where one of the clients is in the clientIds list
        const matters = store.getState().matters;
        if (unsavedEntity && !matters.find(m => m.id === unsavedEntity.id)) matters.push(unsavedEntity);

        return matters.filter(m => {
            const inCommon = m.clients.filter(value => clientIds.includes(value));

            if (matchAll) return inCommon.length === clientIds.length;
            return inCommon.length > 0;
        });
    };

    /**
     * Get benefactors for the given matters
     */
    static getBenefactors = (matters: ArrayMap<Matter> | ArrayMap<Lead> | (Matter | Lead)[], participants?: ArrayMap<Participant> | Participant[]): uuid[] => {
        // Callers that subscribe to the participants slice (e.g. PaymentCard) pass it in so
        // their memoized derivations recompute when participant records sync in. Others fall
        // back to reading the live store.
        const source = participants ?? store.getState().participants;
        const matched = source.filter(p => matters.map((m: EntityType) => m.id).includes(p.entityId));
        return matched.filter(p => p.relationships?.includes(LookupRegistry.SACROSANCT.BENEFACTOR))
            .map(p => p.contactId);
    };

    /**
     * Get payers from leads.
     *
     * @param {Lead[]} leads
     * @return {uuid[]}
     */
    static getLeadPayers = (leads: ArrayMap<Lead> | Lead[]): uuid[] => {
        return util.flatten(leads.map(l => l.payers)
            .filter(p => !!p)
            .filter(util.distinctFilter)
        );
    };

    /**
     * Get a list of all contacts that are also clients.  We do that by looking in two places:
     * 1. clients in matters, but that can change after the case is made, so we also need to look in
     * 2. Participants, specifically for the LookupRegistry.SACROSANCT.CLIENT in relationships.
     *
     * @param {boolean} onlyIds - return on IDs, not full names
     * @return {Array.<Contact|uuid>}}
     */
    static getClients = (onlyIds: boolean): Array<Contact | uuid> => {
        const allClients = [];

        store.getState().matters
            .forEach(matter => {
                // Get the lead for the matter
                allClients.push(matter.clients);

                // For this matter, find all participants
                const participants = store.getState().participants
                    .filter(p => p.entityType === 'matter' && p.entityId === matter.id && p.relationships.includes(LookupRegistry.SACROSANCT.CLIENT))
                    .map(p => p.contactId);

                allClients.push(participants);
            });

        const allIds = util.flatten(allClients).filter(util.distinctFilter);

        if (onlyIds) return allIds;
        else return allIds.map(cid => store.getState().contacts.find(c => c.id === cid)) // Array of contacts
            .sort(sortContacts);
    };

    // ---------------------- E X P E N S E S ------------------//

    /**
     * Get all expenses for the given matterId and optional invoiceId
     *
     * @param {uuid} matterId
     * @param {uuid} [invoiceId]
     * @return {Expense[]}
     */
    static getExpenses = (matterId: uuid, invoiceId?: uuid): Expense[] => {
        let result = [];

        if (matterId) {
            return invoiceId
                   ? store.getState().expenses.getMappingFor('invoiceId', invoiceId)
                   : store.getState().expenses.getMappingFor('matterId', matterId);
        }
        else return result;
    };

    /**
     * Get billable expenses from a matter, with an optional invoiceId
     *
     * @param {uuid} matterId
     * @param {uuid} invoiceId
     */
    static getBillableExpenses = (matterId: uuid, invoiceId?: uuid): Expense[] => {
        return CaseHelper.getExpenses(matterId, invoiceId)
            .filter(expense => !expense.excludeFromInvoice && expense.reimbursable && !expense.invoiceId);
    };

    /**
     * Get all unbilled expenses for a matter.  This means the expense is included in any invoice, finalized or not.
     *
     * @param {uuid} matterId
     * @param {Date} [startDate]
     * @param {Date} [endDate]
     * @return {Expense[]}
     */
    static getUnbilledExpenses = (matterId: uuid, startDate?: Date, endDate?: Date): Expense[] => {
        const _startDate = DateUtil.startOfDay(startDate);
        const _endDate = DateUtil.endOfDay(endDate || new Date());

        return CaseHelper
            .getBillableExpenses(matterId)
            .filter(expense => {
                // Check to make sure this task is not already part of another invoice by getting the latest invoice for this
                // matter, and use its endDate as the filter
                if (!expense.expenseDate) return false;
                return endDate ? DateUtil.isBetween(expense.expenseDate, _startDate, _endDate) : true;
            });
    }

    /**
     * Get all billed expenses, either for all invoices or a specific invoice.
     *
     * @param {uuid} matterId
     * @param {uuid} [invoiceId]
     * @return {Expense[]}
     */
    static getBilledExpenses = (matterId: uuid, invoiceId?: uuid): Expense[] => {
        // TODO: this should be based on finalized invoices
        return CaseHelper.getBillableExpenses(matterId, invoiceId)
            .filter(util.existsFilter)
            .filter(e => e.invoiceId && e.reimbursable);
    }

    /**
     * Get the cost of expenses for a case, or for an optional invoice
     *
     * @param {uuid} matterId
     * @param {uuid} [invoiceId]
     * @returns {number}
     */
    static getExpenseCosts = (matterId: uuid, invoiceId?: uuid): number => {
        return CaseHelper.getBillableExpenses(matterId, invoiceId).reduce(_expenseReducer, 0);
    };

    /**
     * Get the cost of unbilled expenses. No invoiceId needed, as we are specifically looking for expenses not associated with an invoice.
     *
     * @param {Matter} matter
     * @param {Expense[]} [expenses]
     * @return {number}
     */
    static getUnbilledExpensesCost = (matter: Matter, expenses: Expense[]): number => {
        if (expenses) return expenses.reduce(_expenseReducer, 0);
        else return CaseHelper.getUnbilledExpenses(matter.id).reduce(_expenseReducer, 0);
    };

    /**
     * Get the cost of billed expenses, either for a matter in general or an optional specific invoice.
     *
     * @param {Matter} matter
     * @param {uuid} [invoiceId]
     * @return {number}
     */
    static getBilledExpensesCost = (matter: Matter, invoiceId?: uuid): number => {
        return CaseHelper.getBilledExpenses(matter.id, invoiceId).reduce(_expenseReducer, 0);
    };

    /**
     * Get the total expenses for a matter.
     *
     * @param {uuid} matterId
     * @return {number}
     */
    static getExpensesCost = (matterId: uuid): number => {
        const expenses = store.getState().expenses
            .getMappingFor('matterIdBillable', matterId);

        let expenseCosts = 0;
        expenses.forEach(e => {
            expenseCosts += (e.quantity * e.cost);
        });

        return expenseCosts;
    };

    /**
     * Get the amount of paid expenses for a matterId.
     *
     * @param {uuid} matterId
     * @return {number}
     */
    static getPaidExpenses = (matterId: uuid): number => {
        return CaseHelper.getBilledExpenses(matterId).reduce(_amountPaidReducer, 0);
    };


    // ---------------------- T A S K S ------------------//


    /**
     * Get a list of tasks related to this matterId and optional invoiceId.
     *
     * @param {uuid} matterId
     * @param {uuid} [invoiceId]
     * @param {boolean} [sorted] - true if we need then sorted by completionDate ascending
     * @return {Task[]}
     */
    static getTasks = (matterId: uuid, invoiceId?: uuid, sorted?: boolean): Task[] => {
        let result = [];

        if (matterId) {
            if (invoiceId) result = store.getState().tasks.getMappingFor('invoiceId', invoiceId);
            else result = store.getState().tasks.getMappingFor('matterId', matterId);
        }

        if (sorted) return result.sort((a, b) => a.completionDate - b.completionDate);
        else return result;
    }

    /**
     * Get a list of billable tasks related to this matterId and optional invoiceId.
     *
     * @param {uuid} matterId
     * @param {uuid} [invoiceId]
     * @return {Task[]}
     */
    static getBillableTasks = (matterId: uuid, invoiceId?: uuid): Task[] => {
        return CaseHelper.getTasks(matterId, invoiceId).filter(task => task.status !== 'D' && task.billable);
    };

    /**
     * Get all unbilled tasks for a matter.  This means the task is billable and not included in any invoice,
     * finalized or otherwise.
     *
     * @param {uuid} matterId
     * @param {Date} [startDate]
     * @param {Date} [endDate]
     * @return {Task[]}
     */
    static getUnbilledTasks = (matterId: uuid, startDate?: Date, endDate?: Date): Task[] => {
        return CaseHelper
            .getBillableTasks(matterId)
            .filter(task => {
                if (!task.completionDate) return false;
                if (task.invoiceId) return false;

                return endDate ? !DateUtil.isBetween(task.completionDate, startDate, endDate) : true;
            });
    }

    /**
     * Get all billed tasks, either for all invoices or a specific invoice.
     *
     * @param {uuid} matterId
     * @param {uuid} [invoiceId]
     * @return {Task[]}
     */
    static getBilledTasks = (matterId: uuid, invoiceId?: uuid): Task[] => CaseHelper.getBillableTasks(matterId, invoiceId).filter(task => task.invoiceId);

    /**
     * Get the cost of tasks for a case, or for an optional invoice
     *
     * @param {uuid} matterId
     * @param {uuid} [invoiceId]
     * @returns {number}
     */
    static getTaskCosts = (matterId: uuid, invoiceId?: uuid): number => {
        return CaseHelper.getBillableTasks(matterId, invoiceId).reduce(_taskReducer, 0);
    };

    /**
     * Get the cost of unbilled tasks. No invoiceId needed, as we are specifically looking for tasks not associated with an invoice.
     *
     * Special handling is needed for flat fees. We have to return a cost of 0 for tasks related to a flat fee that are not completed.
     * We then need to see which flat fees are completed but not yet billed.
     *
     * @param {Matter} matter
     * @param {Date} [startDate]
     * @param {Date} [endDate]
     * @param {Task[]} [tasks]
     * @return {number}
     */
    static getUnbilledTasksCost = (matter: Matter, startDate?: Date, endDate?: Date, tasks?: Task[]): number => {
        if (!tasks) tasks = CaseHelper.getUnbilledTasks(matter.id);

        let hourlyTasks = tasks.filter(t => !t.flatFeeBucket);
        if (endDate) hourlyTasks = hourlyTasks.filter(t => DateUtil.isBetween(t.completionDate, startDate, endDate));

        const hourlyTaskAmount = hourlyTasks.reduce(_taskReducer, 0);
        const unbilledFlatFees = CaseHelper.getUnbilledEarnedFlatFees(matter, startDate, endDate)
            .reduce((tot, cur) => tot + this.getEarnedFlatFeeAmount(cur.data), 0);

        return hourlyTaskAmount + unbilledFlatFees;
    };

    /**
     * Get the cost of billed tasks, either for a matter in general or an optional specific invoice.
     *
     * @param {Matter} matter
     * @return {number}
     */
    static getBilledTasksCost = (matter: Matter): number => {
        const tasks = CaseHelper.getBilledTasks(matter.id);

        const hourlyTasks = tasks.filter(t => !t.flatFeeBucket);

        const hourlyTaskAmount = hourlyTasks.reduce(_taskReducer, 0);
        const billedFlatFees = tasks.map(t => t.flatFeeBucket)
            .filter(util.existsFilter)
            .filter(util.distinctFilter)
            .map(ffid => store.getState().flatFees.get(ffid))
            .filter(ff => !!ff.invoiceId)// has been invoiced
            .reduce((tot, cur) => tot + this.getEarnedFlatFeeAmount(cur), 0);

        return hourlyTaskAmount + billedFlatFees;
    };

    /**
     * Given a flatFee, get the amount of earned fees (including milestones).
     *
     * @param {FlatFee | FlatFeeMilestone} flatFee - the flat fee to calculate
     * @return {number}
     */
    static getEarnedFlatFeeAmount = (flatFee: FlatFee | FlatFeeMilestone): number => {
        let amount = 0;
        const isFlatFee = flatFee instanceof FlatFee;

        // First check to see if the flat fee has been earned. If so, just return that amount.
        if (flatFee.earned) amount = flatFee.amount;
        else {
            // Check for milestones
            if (isFlatFee) {
                const milestones = store.getState().flatFeeMilestones.getMappingFor('flatFeeId', flatFee.id);

                if (milestones.length > 0) {
                    amount = milestones.reduce((tot, cur) => tot + this.getEarnedFlatFeeAmount(cur), 0);
                }
            }
            else amount = 0;
        }

        return amount;
    }

    /**
     * Get earned flat fees for this matter that have NOT been billed for (not included in any invoice, finalized or not)
     *
     * @param {Matter} matter
     * @param {Date} startDate
     * @param {Date} endDate
     * @return {FlatFeeResult[]}
     */
    static getUnbilledEarnedFlatFees = (matter: Matter, startDate: Date, endDate: Date): FlatFeeResult[] => {
        // Get all invoices for this matter
        const invoices = FinancialHelper.getInvoices(matter.id);

        const inRange = (dt: Date) => {
            return invoices.some(inv => (dt >= inv.startDate) && (dt <= inv.endDate));
        };

        const results = [];

        // Check top-level flat fees
        // const flatFees = matter
        const flatFees = store.getState()
            .flatFees
            .getMappingFor('matterId', matter.id)
            .filter(ff => ff.earned && !ff.invoiceId) // initial filter: earned
            .filter(ff => !inRange(ff.completionDate)) // check completion not in date range of any invoice for this matter
            .map(ff => {
                let amount = ff.amount;

                // If there are completed/billed milestones, we need to remove those amounts from the amount here
                store.getState().flatFeeMilestones.getMappingFor('flatFeeId', ff.id)
                    .forEach(ffm => {
                        if (ffm.earned && inRange(ffm.completionDate)) amount -= ffm.amount;
                    });

                results.push({
                    date: DateUtil.formatDate(ff.completionDate),
                    title: ff.name,
                    amount,
                    type: 'ff',
                    data: ff
                });

                return ff;
            });

        // Check milestones
        store.getState().flatFees.getMappingFor('matterId', matter.id)
            .forEach(ff => {
                const ms = store.getState()
                    .flatFeeMilestones
                    .getMappingFor('flatFeeId', ff.id)
                    .filter(ffm => ffm.earned && !ffm.invoiceId && !inRange(ffm.completionDate))
                    .filter(ffm => !flatFees.some(ff => {
                        return store.getState()
                            .flatFeeMilestones
                            .getMappingFor('flatFeeId', ff.id)
                            .find(ffm1 => ffm1.id === ffm.id);
                    }));

                ms.forEach(ffm => {
                    results.push({
                        date: DateUtil.formatDate(ffm.completionDate),
                        title: `${ff.name} / ${ffm.name}`,
                        amount: ffm.amount,
                        data: ffm,
                        type: 'ffm'
                    });
                });
            });

        if (endDate) {
            return results.filter(ff => DateUtil.isBetween(ff.completionDate, startDate, endDate));
        }
        else return results;
    }

    /**
     * Get the amount of paid tasks for a matterId.
     *
     * @param {uuid} matterId
     * @return {number}
     */
    static getPaidTasks = (matterId: uuid): number => {
        return CaseHelper.getBilledTasks(matterId).reduce(_amountPaidReducer, 0);
    };

    //-------------- F I N A N C I A L S ----------/

    /**
     * Given a matter, get the amount in trust.
     *
     * If a payment, it could be a split.  Check target to see if it is null.  If so,
     * look in paymentDistribution array for the matterId and amount.
     *
     * @param {Matter} matter
     * @return {number}
     */
    static getTrustAmount = (matter: Matter): number => {
        const txns = store.getState().transactions;

        const paymentFilter = (tx: Transaction) => {
            if (tx.txType === 'P' && tx.targetType === 'M') {
                if (tx.target && tx.target === matter.id) return true;
                else if (tx.paymentDistribution.some(pd => pd.matterId === matter.id)) return true;
            }
            else if (tx.txType === 'P' && tx.targetType === 'C') {
                return tx.target && matter.clients.includes(tx.target);
            }
            else return false;
        };

        const paymentReducer = (total: number, tx: Transaction): number => {
            if (tx.target) return total + tx.amount;
            else return Number(tx.paymentDistribution.find(pd => pd.matterId === matter.id).amount);
        };

        // Get all payments to this matter (just the matter, not the clients).
        const payments: number = txns
            .filter(paymentFilter)
            .reduce(paymentReducer, 0);

        const disbursements = txns
            .filter(tx => tx.txType === 'D' && tx.sourceType === 'M' && tx.source === matter.id)
            .reduce((total, tx) => total + tx.amount, 0);

        const transfersIn = txns
            .filter(tx => tx.txType === 'T' && tx.targetType === 'M' && tx.target === matter.id)
            .reduce((total, tx) => total + tx.amount, 0);

        const transfersOut = txns
            .filter(tx => tx.txType === 'T' && tx.sourceType === 'M' && tx.source === matter.id)
            .reduce((total, tx) => total + tx.amount, 0);

        // console.log({payments, disbursements, transfersIn, transfersOut});

        return payments - disbursements + transfersIn - transfersOut;
    };

    //-------------- C O M B I N E D --------------/

    // /**
    //  * Get the total amount billed for a matter or optional invoice.
    //  *
    //  * @param {uuid} matterId
    //  * @param {uuid} [invoiceId]
    //  * @return {number}
    //  */
    // static getTotalBilled = (matterId, invoiceId) => {
    //     return CaseHelper.getBilledExpensesCost(matterId, invoiceId) + CaseHelper.getBilledTasksCost(matterId, invoiceId);
    // };

    /**
     * Get the total amount not yet billed for a matter.  Invoice is not needed as we are looking for items NOT associated with an invoice.
     *
     * @param {Matter} matter
     * @param {Task[]} [tasks]
     * @param {Expense[]} [expenses]
     * @return {number}
     */
    static getTotalUnbilled = (matter: Matter, tasks?: Task[], expenses?: Expense[]): number => {
        return CaseHelper.getUnbilledExpensesCost(matter, expenses) + CaseHelper.getUnbilledTasksCost(matter, null, null, tasks);
    };

    /**
     * Get the total amount not yet billed for a matter.  Invoice is not needed as we are looking for items NOT associated with an invoice.
     *
     * @param {Matter} matter
     * @return {number}
     */
    static getTotalPaid = (matter: Matter): number => {
        return CaseHelper.getPaidTasks(matter.id) + CaseHelper.getPaidExpenses(matter.id);
    };

    /**
     * Format a CaseType's name.
     *
     * @param {CaseType} ct
     * @param {boolean} [short]
     * @returns {string}
     */
    static formatCaseType = (ct: CaseType, short: boolean = false): string => {
        if (!ct) return '';
        return `${ct.category} ${!short && ct.subcategory ? `(${ct.subcategory})` : ''}`;
    };

    /**
     * Get the name of a case type from an ID.
     *
     * @param {uuid} caseTypeId
     * @param {boolean} [short]
     * @returns {string}
     */
    static formatCaseTypeFromId = (caseTypeId: uuid, short: boolean = false): string => {
        if (!caseTypeId) return '';

        let ct = store.getState().caseTypes.get(caseTypeId);
        if (!ct) {
            console.warn(`No case type for ${caseTypeId} using get, ${store.getState().caseTypes.length} case types in store`);
            ct = store.getState().caseTypes.find(c => c.id === caseTypeId);
            if (!ct) {
                console.warn(`No case type found for ${caseTypeId} using find`);
                return '';
            }
        }

        return CaseHelper.formatCaseType(ct, short);
    };

    /**
     * Render a consistent formatting for a case type
     * @param {Matter} matter
     */
    /**
     * Group case types by category.
     */
    static groupCaseTypesByCategory(caseTypes: CaseType[]): Map<string, CaseType[]> {
        const map = new Map<string, CaseType[]>();
        for (const ct of caseTypes) {
            if (!ct.category) continue;
            const group = map.get(ct.category) || [];
            group.push(ct);
            map.set(ct.category, group);
        }
        return map;
    }

    /**
     * Ensure that each category with subcategories has a generic category entry.
     * Called after core data fetch on login.
     */
    static ensureGenericCategoryEntries(caseTypes: ArrayMap<CaseType>): void {
        const firmId = getCurrentFirmId();
        const grouped = CaseHelper.groupCaseTypesByCategory(
            caseTypes.filter(ct => ct.status !== 'D') as CaseType[]
        );

        for (const [category, types] of grouped) {
            const hasSubcategories = types.some(ct => !!ct.subcategory);
            const hasGeneric = types.some(ct => CaseHelper.isGeneric(ct));

            if (hasSubcategories && !hasGeneric) {
                const generic = new CaseType({
                    caseTypeId: genGenericCaseTypeUUID(firmId, category),
                    category,
                    code: CaseHelper.GENERIC_CODE,
                    rateType: 'h',
                    averagePrice: 0,
                    retainer: 0,
                    minimumTrust: 0,
                    firmId,
                    addedBy: getCurrentUserId(),
                    addedOn: newHRTimestamp(),
                    status: 'C'
                });
                store.dispatch(CaseTypeActions.saveCaseTypeQueue(generic));
            }
        }
    }

    /**
     * Check if a question/red flag applies to any of the given case type IDs,
     * considering both individual appliesTo UUIDs and generic category entries.
     */
    static questionAppliesToAnyCaseType(
        q: { appliesTo?: uuid[] },
        caseTypeIds: uuid[],
        allCaseTypes: CaseType[] | ArrayMap<CaseType>
    ): boolean {
        if (!caseTypeIds || caseTypeIds.length === 0) return false;
        if (!q.appliesTo || q.appliesTo.length === 0) return false;

        // O(1) lookup for ArrayMap, O(n) for arrays
        const lookup = (id: string): CaseType | null => {
            if (typeof (allCaseTypes as any).get === 'function')
                return (allCaseTypes as ArrayMap<CaseType>).get(id);
            return (allCaseTypes as CaseType[]).find(c => c.id === id) || null;
        };

        // Build set of categories from the lead's case types
        const leadCategories = new Set<string>();
        for (const ctid of caseTypeIds) {
            const target = lookup(ctid);
            if (target?.category) leadCategories.add(target.category.trim());
        }

        let hasValidEntry = false;

        for (const id of q.appliesTo) {
            // Direct UUID match
            if (caseTypeIds.includes(id)) return true;

            // Look up the appliesTo CaseType
            const ct = lookup(id);
            if (!ct) continue; // Stale UUID — skip

            hasValidEntry = true;
            if (CaseHelper.isGeneric(ct) && ct.category) {
                // Generic: match any lead case type in the same category
                if (leadCategories.has(ct.category.trim())) return true;
            }
        }

        // If ALL entries were stale (not found in store), treat as general question
        if (!hasValidEntry) return true;

        return false;
    }

    /**
     * Smart display: show "All [Category]" for generic entries, individual names for specifics.
     */
    static formatAppliesToDisplay(
        appliesTo: uuid[],
        allCaseTypes: CaseType[] | ArrayMap<CaseType>
    ): string {
        const parts: string[] = [];
        const grouped = CaseHelper.groupCaseTypesByCategory(allCaseTypes.filter(() => true) as CaseType[]);

        if (appliesTo?.length > 0) {
            for (const id of appliesTo) {
                const ct = allCaseTypes.find(c => c.id === id);
                if (!ct) continue;
                if (CaseHelper.isGeneric(ct)) {
                    // Check if this is a single-type category (only the generic exists)
                    const catTypes = grouped.get(ct.category);
                    const hasOnlyGeneric = catTypes && catTypes.filter(c => !CaseHelper.isGeneric(c)).length === 0;
                    parts.push(hasOnlyGeneric ? ct.category : `All ${ct.category}`);
                } else {
                    parts.push(CaseHelper.formatCaseType(ct).trim());
                }
            }
        }

        return parts.join(', ');
    }

    static renderCaseType = (matter: Matter): ReactElement => {
        return <span key={`${matter.id}-case-type`}
                     style={{fontSize: '.9em'}}>{CaseHelper.formatCaseTypeFromId(matter.caseTypeId)}</span>
    }

    static renderDispositionIcon = (matter: Matter) => {
        let icon: IconProp;
        let cls: string;

        if (matter.disposition === 'o') {
            icon = Icons.CASE_OPEN.reactIcon;
            cls = 'CaseOpen';
        }
        else if (matter.disposition === 'c') {
            icon = Icons.CASE_OPEN.reactIcon;
            cls = 'CaseClosed';
        }
        else if (matter.disposition === 'w') {
            icon = Icons.CASE_WITHDRAWN.reactIcon;
            cls = 'CaseWithdrawn';
        }
        else if (matter.disposition === 'p') {
            icon = Icons.CASE_PENDING.reactIcon;
            cls = 'CasePending';
        }
        else if (matter.disposition === 'h') {
            icon = Icons.CASE_ON_HOLD.reactIcon;
            cls = 'CaseOnHold';
        }


        return <FontAwesomeIcon icon={icon} className={cls}/>;
    }

    /**
     * Show an icon and optional matter name given the current status of the matter.
     *
     * @param {Matter} matter - related matter to show the disposition for
     * @param {boolean} showName - include the matter name in the label?
     * @param {boolean} isExport - regular text for exports
     * @param {string} prefix - prefix the case name with this string
     * @returns {string|ReactElement}
     */
    static renderDisposition = (matter: Matter, showName: boolean, isExport: boolean, prefix: string = ''): string | ReactElement => {
        const label = showName ? <StateLabel key={matter.id}
                                             item={matter}
                                             title={util.getErrorMessage(matter)}>{`${prefix} ${matter.name}`}</StateLabel>
                               : null;

        if (matter.disposition === 'o') {
            if (isExport) return 'Open';
            else return <IconButton key={`${matter.id}-open`}
                                    type={Icons.CASE_OPEN}
                                    label={label} title='Open'
            />;
        }
        else if (matter.disposition === 'c') {
            if (isExport) return 'Closed';
            else return <IconButton key={`${matter.id}-closed`}
                                    type={Icons.CASE_CLOSED}
                                    label={label}
                                    title='Closed'
            />;
        }
        else if (matter.disposition === 'w') {
            if (isExport) return 'Withdrawn';
            else return <IconButton
                key={`${matter.id}-withdrawn`}
                type={Icons.CASE_WITHDRAWN}
                label={label}
                title='Withdrawn'
            />;
        }
        else if (matter.disposition === 'p') {
            if (isExport) return 'Pending';
            else return <IconButton key={`${matter.id}-pending`}
                                    type={Icons.CASE_PENDING}
                                    label={label}
                                    title='Pending'
            />;
        }
        else if (matter.disposition === 'h') {
            if (isExport) return 'On Hold';
            return <IconButton key={`${matter.id}-on-hold`}
                               type={Icons.CASE_ON_HOLD}
                               label={label}
                               title='On Hold'
            />;
        }

        else return null;
    }

    /**
     * Get the number of matters with the withdrawn flag where the matter is of the given type.
     *
     * @param {RedFlagType} redFlagType
     * @returns {number}
     */
    static getWithdrawnStats = (redFlagType: RedFlagType): number => {
        const matters = store.getState().matters.filter(matter => {
            // Keep only matters that actually have this red flag *checked*. Note: every matter is
            // seeded with an (unchecked) flag for every type, so matching redFlagTypeId alone would
            // match nearly everything — the meaningful signal is checked (mirrors RedFlagAnalytics
            // checkDate, which gates on rf.checked). getRedFlags already excludes status 'D' rows,
            // but we re-assert it defensively.
            return getRedFlags(matter.id).some(rf => rf.redFlagTypeId === redFlagType.id && rf.checked && rf.status !== 'D');
        });

        return matters.filter(matter => matter.disposition === 'w').length;
    }

    /**
     * Get flat fees for the given matter.
     *
     * @param {Matter} matter
     * @returns {FlatFee[]}
     */
    static getFlatFees(matter: Matter): FlatFee[] {
        return store.getState().flatFees.getMappingFor('matterId', matter.id);
    }

    /**
     * Create a cloned set of flat fees and flat fee milestones for a given matter and case type.
     *
     * @param {Matter} matter
     * @param {uuid} caseTypeId
     * @returns {void}
     */
    static createFlatFeesFromTemplate(matter: Matter, caseTypeId: uuid): void {
        // Look for flat fee templates to add
        store.getState().flatFees
            .getMappingFor('caseTypeId', caseTypeId)
            .sort(util.sortByPosition)
            .forEach(ff => {
                const flatFeeId = util.genUUID();

                const {name, amount, description, isDefault, position} = ff;

                const newFF = new FlatFee({
                    // Clone them and change their owners
                    firmId: getCurrentFirmId(),
                    flatFeeId,
                    addedBy: getCurrentUserId(),
                    addedOn: newHRTimestamp(),
                    status: 'C',
                    entityType: 'matter',
                    entityId: matter.id,
                    name, amount, description, isDefault, position
                });

                store.dispatch(FlatFeeActions.saveFlatFeeQueue(newFF));

                store.getState().flatFeeMilestones
                    .getMappingFor('flatFeeId', ff.id)
                    .sort(util.sortByPosition)
                    .forEach((ffm) => {
                        const flatFeeMilestoneId = util.genUUID();

                        const {name, amount, description, percentage, position} = ffm;

                        const newFFM = new FlatFeeMilestone({
                            // Clone them and change their owners
                            firmId: getCurrentFirmId(),
                            flatFeeMilestoneId,
                            addedBy: getCurrentUserId(),
                            addedOn: newHRTimestamp(),
                            status: 'C',
                            entityType: 'flatFee',
                            entityId: flatFeeId,
                            name, amount, description, percentage, position
                        });

                        store.dispatch(FlatFeeMilestoneActions.saveFlatFeeMilestoneQueue(newFFM));
                    });
            });
    }
}

export default CaseHelper;
